import React from 'react';
import {
  ProviderClientPhoneType,
  IProviderClientPhone,
  IProviderClient,
} from '../../../api/Providers/Clients/types';
import ImportContactsModal from '../../../components/provider/clients/EmptyClientList/ImportContactsModal';
import { ModalTrigger } from '../../../components/ui';
import ManagedModal from '../../../components/ui/ManagedModal';
import { APP, ENABLE_APP_SIMULATOR } from '../../../config';
import { getAndroidVersion } from '../../AppInfo';
import { isValidEmail, isValidPhone } from '../../validators';
import { IUIContactData, IUIContactPhone } from '../UIContact';
import type { ContactsNavigator } from './cordovaPluginContacts.types';

const Nav: ContactsNavigator = navigator as unknown as ContactsNavigator;

type ImportedPhone = {
  value: string;
  type: string;
};

type Phones = {
  mobile?: Partial<IUIContactPhone>;
  home?: Partial<IUIContactPhone>;
};

type ImportedEmail = {
  value: string;
};

type ImportedName = {
  formatted?: string;
};

type ImportedContact = {
  id: number;
  name: { formatted: string; };
  emails: ImportedEmail[];
  phoneNumbers: ImportedPhone[];
};

export type ParsedContact = Pick<IUIContactData,
'id'
| 'name'
| 'phones'
> & {
  notifications?: { sms: boolean };
  email?: string;
};

const nameIsValid = (
  name?: ImportedName,
): boolean => name && !!name.formatted;

const hasEmailAddress = (
  emails: ImportedEmail[],
): boolean => emails && emails.length > 0;

const hasPhoneNumber = (
  phoneNumbers: ImportedPhone[],
): boolean => phoneNumbers && phoneNumbers.length > 0;

const hasContactMethod = (
  contactData: ImportedContact,
): boolean => (
  hasEmailAddress(contactData.emails) || hasPhoneNumber(contactData.phoneNumbers)
);

const isValidContact = (
  contactData: ImportedContact,
): boolean => (
  nameIsValid(contactData.name) && hasContactMethod(contactData)
);

const parseEmail = (
  contact: ImportedContact,
): string | null => {
  const { emails } = contact;

  if (emails && emails.length > 0 && isValidEmail(emails[0].value)) {
    return contact.emails[0].value;
  }

  return null;
};

const parsePhoneNumbers = (
  contact: ImportedContact,
): Phones => {
  const phones: Phones = {};
  const { phoneNumbers } = contact;

  if (phoneNumbers && phoneNumbers.length > 0) {
    const mobileNumber = phoneNumbers.find(pn => pn.type === 'mobile');
    const homeNumber = phoneNumbers.find(pn => pn.type === 'home');

    if (mobileNumber && isValidPhone(mobileNumber.value)) {
      phones.mobile = {
        number: mobileNumber.value,
        type: ProviderClientPhoneType.Mobile,
      };
    }

    if (homeNumber && isValidPhone(homeNumber.value)) {
      phones.home = {
        number: homeNumber.value,
        type: ProviderClientPhoneType.Home,
      };
    }

    if (!Object.keys(phones).length && isValidPhone(phoneNumbers[0].value)) {
      phones.mobile = {
        number: phoneNumbers[0].value,
        type: ProviderClientPhoneType.Mobile,
      };
    }
  }

  return phones;
};

const parseImportedContact = (
  contact: ImportedContact,
): ParsedContact => ({
  id: contact.id,
  name: contact.name.formatted,
  email: parseEmail(contact),
  phones: parsePhoneNumbers(contact),
  notifications: {
    sms: true,
  },
});

const getFilterFields = () => {
  if (Nav?.contacts) {
    return [Nav.contacts.fieldType.name];
  }
  return [];
};

export const testContacts: ImportedContact[] = [
  {
    id: 0,
    name: { formatted: 'Lyons West' },
    emails: [{ value: 'lyonswest@quonata.com' }],
    phoneNumbers: [],
  },
  {
    id: 1,
    name: { formatted: 'Watson Gill' },
    emails: [],
    phoneNumbers: [{ value: '(809) 500-3156', type: 'mobile' }],
  },
  {
    id: 2,
    name: { formatted: 'Johns Weber' },
    emails: [{ value: 'johnsweber@quonata.com' }],
    phoneNumbers: [{ value: '(971) 415-2113', type: 'mobile' }],
  },
  {
    id: 3,
    name: { formatted: 'Flowers Flowers' },
    emails: [{ value: 'flowersflowers@quonata.com' }],
    phoneNumbers: [{ value: '(839) 594-3182', type: 'mobile' }],
  },
  {
    id: 4,
    name: { formatted: 'Christian Kidd' },
    emails: [{ value: 'christiankidd@quonata.com' }],
    phoneNumbers: [{ value: '(896) 478-2324', type: 'mobile' }],
  },
  {
    id: 5,
    name: { formatted: 'Bolton Hood' },
    emails: [{ value: 'boltonhood@quonata.com' }],
    phoneNumbers: [{ value: '(916) 524-3721', type: 'mobile' }],
  },
  {
    id: 6,
    name: { formatted: 'Jennifer Fernandez' },
    emails: [{ value: 'jenniferfernandez@quonata.com' }],
    phoneNumbers: [{ value: '(835) 445-2615', type: 'mobile' }],
  },
  {
    id: 7,
    name: { formatted: 'Summers Odom' },
    emails: [{ value: 'summersodom@quonata.com' }],
    phoneNumbers: [{ value: '(951) 485-3252', type: 'mobile' }],
  },
  {
    id: 8,
    name: { formatted: 'Marianne Tyler' },
    emails: [{ value: 'mariannetyler@quonata.com' }],
    phoneNumbers: [{ value: '(812) 489-2817', type: 'mobile' }],
  },
  {
    id: 9,
    name: { formatted: 'Lottie Singleton' },
    emails: [{ value: 'lottiesingleton@quonata.com' }],
    phoneNumbers: [{ value: '(857) 419-2496', type: 'mobile' }],
  },
  {
    id: 10,
    name: { formatted: 'Stephenson Salas' },
    emails: [{ value: 'stephensonsalas@quonata.com' }],
    phoneNumbers: [{ value: '(847) 466-2474', type: 'mobile' }],
  },
];

export const ERROR_CONTACT_READS_DISABLED = 20;

/**
 * Check if importing device contacts is supported on current platform
 *
 * @returns {Boolean}
 */
export const bulkImportSupported: () => boolean = () => APP || ENABLE_APP_SIMULATOR;

/**
 * TODO: when migrating a consumer of this function
 * move the rendering logic to React
 *
 * Opens a standard modal dialog with a link to the app store
 * with copy about importing contacts. Used in cases where
 * platform does not support importing device contacts.
 */
export const openAppDownloadDialog = () => {
  const appDownloadModal = new ManagedModal({
    triggerType: ModalTrigger,
    renderModal: (onClose => React.createElement(ImportContactsModal, {
      onClose,
    })),
  });

  appDownloadModal.open();
};

export const loadContacts = (): Promise<ParsedContact[]> => {
  let resolve;
  let reject;
  const result: Promise<ParsedContact[]> = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });

  if (APP) {
    const fields = getFilterFields();

    Nav?.contacts.find(fields, contacts => {
      const filteredContacts: ParsedContact[] = (contacts || [])
        .filter(isValidContact)
        .map(parseImportedContact);

      // Check android contacts permission.  Instead of throwing an error, android returns
      // an empty array if permissions are off.
      if (getAndroidVersion() && contacts.length === 0) {
        reject({ code: 20 });
      } else {
        resolve(filteredContacts);
      }
    }, error => {
      reject(error);
    }, {
      desiredFields: [
        Nav.contacts.fieldType.id,
        Nav.contacts.fieldType.name,
        Nav.contacts.fieldType.phoneNumbers,
        Nav.contacts.fieldType.emails,
      ],
      multiple: true,
    });
  } else if (ENABLE_APP_SIMULATOR) {
    resolve(testContacts.map(parseImportedContact));
  }
  return result;
};

const countPhone = (phones?: IProviderClientPhone[]): 1 | 0 => (
  phones && phones.length ? 1 : 0
);

const countEmail = (email?: string): 1 | 0 => (email ? 1 : 0);

type BulkImportStats = {
  phone: number;
  email: number;
  total: number;
};

/**
 * Calculates the percentage of contacts with phone numbers
 * and the percentage of contacts with emails, returned as an object
 */
export const getBulkImportStats = (
  contacts: Pick<IProviderClient, 'phones' | 'email'>[],
): BulkImportStats => {
  const phoneAndEmailCount = contacts.reduce((counter, contact) => ({
    phone: counter.phone + countPhone(contact.phones),
    email: counter.email + countEmail(contact.email),
  }), { email: 0, phone: 0 });

  return {
    phone: phoneAndEmailCount.phone,
    email: phoneAndEmailCount.email,
    total: contacts.length,
  };
};

function calculatePosition(
  groupedContacts: Record<string, ParsedContact[]>,
  letters: Array<string>,
): Record<string, number> {
  const letterPositions = {};
  let itemsSoFar = 0;
  let lettersSoFar = 0;

  letters.forEach(letter => {
    letterPositions[letter] = lettersSoFar + itemsSoFar;
    lettersSoFar += 1;
    itemsSoFar += groupedContacts[letter].length;
  });

  return letterPositions;
}

function groupByFirstLetter(
  list: ParsedContact[],
): Record<string, ParsedContact[]> {
  const result: Record<string, ParsedContact[]> = {};

  list.forEach(contact => {
    let key = contact.name.charAt(0);

    if (key.match(/[0-9]/i)) {
      key = '#';
    } else {
      key = key.toUpperCase();
    }

    if (result[key]) {
      result[key].push(contact);
    } else {
      result[key] = [contact];
    }
  });

  return result;
}

type GroupedContactsResult = {
  contacts: Record<string, ParsedContact[]>,
  letters: Array<string>,
  letterPositions: Record<string, number>,
};

export function getGroupedContacts(contacts: ParsedContact[]): GroupedContactsResult {
  const sortedContacts = [...contacts].sort((a, b) => a.name.localeCompare(b.name));
  const groupedContacts = groupByFirstLetter(sortedContacts);
  const letters = Object.keys(groupedContacts).sort((a, b) => {
    if (a === '#') { return 1; }
    if (b === '#') { return -1; }
    return a.localeCompare(b);
  });

  return {
    contacts: groupedContacts,
    letters,
    letterPositions: calculatePosition(groupedContacts, letters),
  };
}

function splitToChunks<T>(fullList: T[], chunkSize: number = 50): Array<T[]> {
  let remainingContacts = fullList.length;
  let startIndex = 0;
  const processChunks: Array<T[]> = [];

  do {
    const endIndex = startIndex + Math.min(remainingContacts, chunkSize);
    const processChunk = fullList.slice(startIndex, endIndex);
    const size = processChunk.length;
    remainingContacts -= size;
    startIndex += size;
    processChunks.push(processChunk);
  } while (remainingContacts > 0);

  return processChunks;
}

export type ContactImportResult = {
  success: ParsedContact[],
  error: ParsedContact[],
  results: IProviderClient[],
};

export async function batchProcessContacts(
  selectedContacts: ParsedContact[],
  batchProcessChunk: (chunk: ParsedContact[]) => Promise<IProviderClient[]>,
): Promise<ContactImportResult> {
  // split our selected contacts into chunks for upload
  const processChunks = splitToChunks(selectedContacts);

  let allProcessedContacts: Array<IProviderClient> = [];
  const erroredContacts: Array<ParsedContact> = [];
  let successfulContacts: Array<ParsedContact> = [];

  await processChunks.map(async processChunk => {
    try {
      const processedContacts = await batchProcessChunk(processChunk);

      allProcessedContacts = [
        ...allProcessedContacts,
        ...processedContacts,
      ];
      successfulContacts = [
        ...successfulContacts,
        ...processChunk,
      ];
    } catch (err) {
      if (!Array.isArray(err)) {
        throw err;
      } else {
        const retryChunk = [];

        err.forEach((errorObj, idx) => {
          if (Object.keys(errorObj).length) {
            // failed at importing the item at index `idx` in `importChunk`
            erroredContacts.push(processChunk[idx]);
          } else {
            // if there was not specifically an error at that index, try the import again
            retryChunk.push(processChunk[idx]);
          }
        });

        processChunks.push(retryChunk);
      }
    }
  });

  return {
    error: erroredContacts,
    success: successfulContacts,
    results: allProcessedContacts,
  };
}
